import { canSeeSection } from '../../utils/managerAccess'
import { ReactNode, useEffect, useState, type CSSProperties } from 'react'
import { REPORT_SECTIONS } from '../../utils/reportSections'
import { NavLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useUpgrade } from '../../context/UpgradeContext'
import { useOrders } from '../../context/OrdersContext'
import { SyncBadge } from '../SyncBadge'
import { useStuckRows } from '../../hooks/useStuckRows'
import { BuildStamp } from '../BuildStamp'
import { PullToRefresh } from '../PullToRefresh'
import { SetupProgress } from '../SetupProgress'
import { TrialBanner } from '../TrialBanner'
import { LegalGate } from '../LegalGate'
import { useMyWaiting } from '../../hooks/useMyWaiting'
import { greeting } from '../../utils/greeting'
import { useWaitAlerts } from '../../hooks/useWaitAlerts'
import { ConfirmDialog, type ConfirmSpec } from '../pos/ConfirmDialog'
import { TableCalls, QR_DEMO } from '../pos/TableCalls'
import {
  PanelLeftIcon,
  PanelRightIcon,
  ChevronDownIcon,
  LogOutIcon,
  ClipboardListIcon,
  NotebookPenIcon,
  RepeatIcon,
  TableIcon,
  ChefHatIcon,
  BanknoteArrowUpIcon,
  UtensilsIcon,
  TrendingUpIcon,
  SettingsIcon,
  SunMoonIcon,
  WalletIcon,
  XIcon,
  PhHandWavingIcon,
  PrinterIcon,
  EyeIcon,
  ScanBarcodeIcon,
  LockIcon,
} from '../icons'
import { useTheme } from '../../hooks/useTheme'
import './POSLayout.css'
import { VIEW_MODE_EVENT, isViewMode } from '../../services/viewMode'

interface Props {
  children: ReactNode
  title: string
  /** Page-specific controls, hoisted into the top bar. */
  actions?: ReactNode
  /**
   * Üst çubuk, sol menü ve logo yok (ana ekran, 18 Eki 2026: "bu ilk ekran
   * olsun, menü uygulamanın içine girince görünsün"). Yasal onay, deneme
   * uyarısı ve kurulum çubuğu yine çiziliyor; onlar atlanmamalı.
   */
  bare?: boolean
}

/**
 * The settings screen's sections, in the order that screen lists them.
 *
 * Here rather than exported from SettingsPage: the sidebar needs the labels
 * before that page is loaded, and importing the page for a list of six
 * strings would pull the whole settings screen into the layout's bundle.
 * Drifting out of step shows up immediately, a wrong id opens nothing.
 */
const SETTINGS_SECTIONS: [string, string][] = [
  ['hesap', 'Hesabım'],
  ['receipt', 'Fiş'],
  ['staff', 'Kullanıcılar'],
  ['screens', 'Mutfak ekranları'],
  ['kiosk', 'Kiosk'],
  ['sadakat', 'Sadakat'],
  ['kampanya', 'Kampanyalar'],
  ['qr', 'QR Menü'],
  ['printers', 'Yazıcılar'],
]

export function POSLayout({ children, title, actions, bare = false }: Props) {
  const { admin, isOwner, can, canOpenSettings, canSettle, isKitchen, hasFeature, settingsLoading, tenantName, tenantLogo, roleLabels, logout, isSuperadmin, usePrinter } = useAuth()
  const askUpgrade = useUpgrade()
  const { pendingCount } = useOrders()
  const stuck = useStuckRows()
  const { dark, toggle } = useTheme()
  const navigate = useNavigate()
  const { tenant } = useParams()
  const [open, setOpen] = useState(false)
  /** Görüntüleme modunda engellenen bir işlem: kısa uyarı. */
  const [blocked, setBlocked] = useState(false)
  useEffect(() => {
    let t = 0
    const on = () => {
      setBlocked(true)
      window.clearTimeout(t)
      t = window.setTimeout(() => setBlocked(false), 2600)
    }
    window.addEventListener(VIEW_MODE_EVENT, on)
    return () => {
      window.removeEventListener(VIEW_MODE_EVENT, on)
      window.clearTimeout(t)
    }
  }, [])
  const track = useMyWaiting()
  // Takip sayfasında şerit yok: garson zaten listeye bakıyor, süre orada yazıyor.
  const onTrack = useLocation().pathname.endsWith('/takip')
  /** Sipariş alma ekranı ("/:tenant"): barkod düğmesi yalnız orada. */
  const onOrder = useLocation().pathname.replace(/\/$/, '') === `/${tenant}`
  // Selam saate göre (Günaydın, Selam, İyi akşamlar): uygulama açık kalırken
  // de dönsün diye dakikada bir bakılıyor.
  const [hour, setHour] = useState(() => new Date().getHours())
  useEffect(() => {
    const t = window.setInterval(() => setHour(new Date().getHours()), 60_000)
    return () => window.clearInterval(t)
  }, [])
  const trackAlert = useWaitAlerts(!canSettle && track.enabled && !onTrack, track.tables, track.now)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const [ask, setAsk] = useState<ConfirmSpec | null>(null)

  const { pathname } = useLocation()
  const [search] = useSearchParams()

  const onSettings = pathname.endsWith('/ayarlar')
  const onReport = pathname.endsWith('/rapor')
  const onKitchen = pathname.endsWith('/mutfak')

  // Rapor grafiklerinin kodu boşta önceden iniyor: Rapor açılınca grafikler
  // sonradan "patlayarak" gelmesin.
  const canReports = can('reports')
  useEffect(() => {
    if (!canReports) return
    const load = () => void import('../report/RangeCharts')
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number }
    if (w.requestIdleCallback) w.requestIdleCallback(load)
    else window.setTimeout(load, 3000)
  }, [canReports])
  /**
   * Whether the settings sections are listed.
   *
   * Derived from the address rather than kept in state: the list is open
   * whenever the settings screen is the one showing, so pressing Ayarlar
   * opens both at once. Held in state it took two presses, the first
   * navigated and the second expanded, and arriving by a bookmark or the
   * back button left the list collapsed under the item that was lit up.
   *
   * `closed` is the one thing state is needed for: collapsing the list
   * without leaving the screen. Cleared on every navigation, since a press
   * that goes somewhere else has already answered the question.
   */
  const [closedOnSettings, setClosedOnSettings] = useState(false)

  /**
   * Whether the panel is narrowed to an icon rail on a wide screen.
   *
   * Remembered per device rather than per account: it answers "how much room
   * does this screen have", and the same person moving between the counter
   * laptop and a tablet wants a different answer on each.
   */
  const [rail, setRail] = useState(() => {
    try {
      return localStorage.getItem('adissoo.rail') === '1'
    } catch {
      // Private browsing, or storage turned off. The full panel is the right
      // fallback, it is the one that says what everything is.
      return false
    }
  })

  const toggleRail = () => {
    setRail((v) => {
      const next = !v
      try {
        localStorage.setItem('adissoo.rail', next ? '1' : '0')
      } catch {
        // As above: the panel still narrows, it just forgets by morning.
      }
      return next
    })
  }
  const settingsOpen = onSettings && !closedOnSettings

  useEffect(() => {
    if (!onSettings) setClosedOnSettings(false)
  }, [onSettings])

  const onLogout = () => {
    // Takılan (sunucunun reddettiği) kayıtlar çıkışı engellemiyor: internet
    // gelince de gitmeyecekler. Yine de söyleniyor.
    if (pendingCount === 0 && stuck.length > 0) {
      setAsk({
        title: `${stuck.length} kayıt gönderilemedi`,
        body: 'Bu kayıtları sunucu kabul etmedi; çoğu zaman adisyon başka bir cihazdan kapatılmıştır. Üst çubuktaki kırmızı rozete basarak görebilir, silebilirsiniz. Çıkış yaparsanız bu cihazda kalırlar.',
        confirmLabel: 'Yine de çıkış yap',
        onConfirm: async () => {
          await logout()
          navigate('/giris', { replace: true })
        },
      })
      return
    }
    if (pendingCount > 0) {
      setAsk({
        title: `${pendingCount} sipariş henüz kaydedilmedi`,
        body: 'Çıkış yaparsanız tekrar giriş için internet gerekir. Önce bağlantının gelmesini bekleyin.',
        confirmLabel: 'Tamam',
      })
      return
    }
    setAsk({
      title: 'Çıkış yapılsın mı?',
      body: 'Tekrar giriş yapmak için kullanıcı adınız ve PIN’iniz gerekir.',
      confirmLabel: 'Çıkış yap',
      onConfirm: async () => {
        await logout()
        navigate('/giris', { replace: true })
      },
    })
  }

  // Sol üstte işletmenin logosu, yüklenmemişse Adissoo (kullanıcı, 2 Kas 2026).
  const brandMark = tenantLogo ? (
    <img src={tenantLogo} alt="" className="drawer__logo drawer__logo--tenant" width={28} height={28} />
  ) : (
    <img src="/icon-192.png" alt="" className="drawer__logo" width={24} height={24} />
  )

  return (
    <div className={`pos ${bare ? 'pos--bare' : ''}`}>
      {/* Every screen inside the layout gets the gesture, which is what makes
          it a reliable way to pick up a new build mid-service. */}
      <PullToRefresh />

      {/* Görüntüleme modu (sistem yöneticisi, 21 Eki 2026): sabit etiket,
          düzenin akışına girmiyor. */}
      {isSuperadmin && (
        <div className="viewbar" role="status">
          <EyeIcon />
          <span>
            <b>Görüntüleme modu:</b> {tenantName}
          </span>
          <button type="button" onClick={() => navigate('/sistem-9f3a')}>
            Çık
          </button>
          {blocked && <em className="viewbar__warn">Görüntüleme modunda değişiklik yapılamaz.</em>}
        </div>
      )}

      {!bare && (
      <header className="bar">
        <button className="bar__burger" aria-label="Menü" onClick={() => setOpen(true)}>
          <i />
          <i />
          <i />
        </button>
        {/* A waiter's screen isn't a till, and on a shared phone the name
            settles who is holding it. The role label comes from data so a yurt
            that calls them something else sees its own word. */}
        <div className="bar__title">
          {canSettle ? (
            title
          ) : admin &&
            (admin.role === 'waiter' ||
              // Mutfak personeli de (20 Eki 2026); tablet ekran hesabı değil,
              // onun adı bir cihaz adı ("Mutfak Tableti").
              (admin.role === 'kitchen' && !admin.username.startsWith('ekran-'))) ? (
            // El sallayan simge, selam ve ilk ad (kullanıcı, 20 Eki 2026); rol
            // ibaresi çekmecede zaten var.
            <span className="bar__hello">
              {greeting(admin.displayName || admin.username, hour)}
              <span className="bar__wave" aria-hidden="true">
                <PhHandWavingIcon />
              </span>
            </span>
          ) : (
            <>
              {admin ? roleLabels[admin.role] : title}
              {admin && (
                <span className="bar__who">
                  {', '}
                  {admin.displayName || admin.username}
                </span>
              )}
            </>
          )}
        </div>
        <div className="bar__spacer" />
        <div className="bar__slot">
          {actions}
          {/* Sipariş Takip kısayolu, durum etiketinin solunda. Sayı, götürülecek
              ürünü olan masa sayısı. */}
          {/* İki yönlü kısayol: sipariş ekranında takip listesine, takipte
              sipariş ekranına. Köşedeki döngüsel ok bunu söylüyor; sayı,
              götürülecek ürünü olan masa sayısı (yalnız sipariş ekranında). */}
          {/* Garsonun kamerayla barkod okutması (20 Eki 2026): sipariş
              ekranında, geçiş düğmesinin solunda. Pencereyi satış ekranı
              açıyor; burası yalnız olayı yayıyor. Patron ve kasiyerde de
              (29 Eki 2026): telefondan ya da tabletten giren patronun USB
              okuyucusu yok, barkod okutamıyordu. */}
          {/* QR Sipariş (30 Eki 2026): masa çağırınca zil, barkod düğmesinin
              solunda; bekleyen istek yokken görünmüyor. Garsonda; kasa ve
              patronda yalnız salonuna bakan garson olmayan masalarınki
              (kullanıcı, 2 Kas 2026: "garson yoksa kasaya"). Görüntüleme
              modunda yok. */}
          {admin && admin.role !== 'kitchen' && !isViewMode() && (hasFeature('qrsiparis') || QR_DEMO) && <TableCalls />}
          {!isKitchen && onOrder && (
            <button
              type="button"
              className="bar__track bar__scan"
              onClick={() => window.dispatchEvent(new Event('adissoo:scan'))}
              aria-label="Barkod okut"
              title="Barkod okut"
            >
              <ScanBarcodeIcon />
            </button>
          )}
          {!canSettle && track.enabled && (
            <NavLink
              to={onTrack ? `/${tenant}` : `/${tenant}/takip`}
              // Geçmişe yeni sayfa eklemeden (20 Eki 2026): iki ekran arasında
              // gidip gelmek "yeni sayfa" sayılıyordu, iPhone'da soldan sağa
              // çekince (geri hareketi) öbür ekrana dönüyordu.
              replace
              className="bar__track"
              aria-label={
                onTrack
                  ? 'Sipariş ala dön'
                  : `Sipariş takip${track.tables.length ? `, ${track.tables.length} masa bekliyor` : ''}`
              }
              title={onTrack ? 'Sipariş Al' : 'Sipariş Takip'}
            >
              {onTrack ? <NotebookPenIcon /> : <ClipboardListIcon />}
              <span className="bar__track-swap" aria-hidden="true">
                <RepeatIcon />
              </span>
              {!onTrack && track.tables.length > 0 && <b>{track.tables.length}</b>}
            </NavLink>
          )}
          {/* Son fişler (24 Eki 2026): rulo bitince basılmayan ya da yarım
              çıkan fişi tekrar basmak için. Yalnız Mutfak sekmesinde
              (kullanıcı); liste fişi basan kasa bilgisayarında, o yüzden
              mutfak tabletinde (kasa yetkisi yok) görünmüyor. */}
          {canSettle && onKitchen && usePrinter && (
            <button
              type="button"
              className="bar__recent"
              onClick={() => window.dispatchEvent(new Event('adissoo:recent-slips'))}
              aria-label="Son fişler"
              title="Son fişleri tekrar bas"
            >
              <PrinterIcon />
              <span>Son fişler</span>
            </button>
          )}
          <SyncBadge />
        </div>
      </header>
      )}

      {!bare && (
      <>
      <div className={`scrim ${open ? 'on' : ''}`} onClick={() => setOpen(false)} />

      <aside className={`drawer ${open ? 'on' : ''} ${rail ? 'is-rail' : ''}`}>
        {/* All three come from data, not the code: the person's display name
            and the role label are editable, and the yurt shows its proper
            name rather than its id. */}
        {/* Escape closes it and so does tapping the scrim, but neither is
            discoverable on a phone, the drawer needs a visible way out. */}
        <button
          type="button"
          className="drawer__close"
          onClick={() => setOpen(false)}
          aria-label="Menüyü kapat"
        >
          <XIcon />
        </button>

        {/* The mark, then who is signed in, the order Cloudflare uses, and
            the reason is the same: the logo says which app this is at a
            glance, the name below says which account it is showing. In the
            rail it is the only thing left, which is what keeps the narrowed
            panel from reading as an unlabelled strip of icons. */}
        <div className="drawer__brand">
          {/* Logo ana ekrana götürüyor (18 Eki 2026); ana ekran yalnız kasa
              yetkisi olanlarda. */}
          {canSettle ? (
            <NavLink to={`/${tenant}/ana`} aria-label="Ana ekran" className="drawer__logo-link">
              {brandMark}
            </NavLink>
          ) : (
            brandMark
          )}
          {/* Kurum üstte, kişi altında. Kasada aynı anda birden fazla kurum
              açık olabiliyor ve ekrana bakan ilk soruyu "hangi işletmedeyim"
              diye soruyor; kim olduğunu zaten biliyor. */}
          <div className="drawer__user">
            <div className="nm">{tenantName}</div>
            <div className="rl">
              {admin?.displayName || admin?.username}
              {admin ? `, ${roleLabels[admin.role]}` : ''}
            </div>
          </div>
        </div>

        <nav className="drawer__links" onClick={() => setOpen(false)}>
          {/* Mutfak personeli yalnız mutfak ekranını görür. */}
          {isKitchen ? (
            <NavLink to={`/${tenant}/mutfak`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('kitchen')}>
              <ChefHatIcon />
              <span>Mutfak</span>
            </NavLink>
          ) : (
          <>
          {canSettle && (
            <NavLink to={`/${tenant}/ana`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('home')}>
              <HomeIcon />
              <span>Ana Ekran</span>
            </NavLink>
          )}
          <NavLink to={`/${tenant}`} end className={({ isActive }) => (isActive ? 'on' : '')} style={tone('sale')}>
            {/* Garson için bu ekran kasa değil, sipariş alma yeri: ikon da
                para değil, kalem ve defter. */}
            {canSettle ? <BanknoteArrowUpIcon /> : <NotebookPenIcon />}
            <span>{canSettle ? 'Gel Al - Kasa' : 'Sipariş Al'}</span>
          </NavLink>
          {!canSettle && track.enabled && (
            <NavLink to={`/${tenant}/takip`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('track')}>
              <ClipboardListIcon />
              <span>Sipariş Takip</span>
              {track.tables.length > 0 && <b className="drawer__count">{track.tables.length}</b>}
            </NavLink>
          )}
          {/* Yalnız patron (kullanıcı, 21 Eki 2026): eskiden kasa da menüyü
              düzenliyordu; kasiyerin ekranından Menü ve Mutfak kaldırıldı. */}
          {can('menu') && (
            <NavLink to={`/${tenant}/menu`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('menu')}>
              <UtensilsIcon />
              <span>Menü</span>
            </NavLink>
          )}
          {/* Menü'nün altında, Ayarlar'ın içinde değil (kullanıcı, 6 Eki
              2026): masa ve salon düzeni sık değişiyor. Yalnız patron, eskiden
              Ayarlar'da olduğu gibi. */}
          {can('settings') && (
            <NavLink to={`/${tenant}/masalar`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('tables')}>
              <TableIcon />
              <span>Masalar</span>
            </NavLink>
          )}
          {/* Mutfak ekranı: patron da açabilir (tezgahları izlemek için); kasiyer
              değil (kullanıcı, 21 Eki 2026). */}
          {isOwner &&
            (hasFeature('mutfak') ? (
              <NavLink to={`/${tenant}/mutfak`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('kitchen')}>
                <ChefHatIcon />
                <span>Mutfak</span>
              </NavLink>
            ) : (
              !settingsLoading && (
                <LockedLink label="Mutfak" icon={<ChefHatIcon />} style={tone('kitchen')} onClick={() => askUpgrade('mutfak')} />
              )
            ))}
          {/* Beside the menu rather than under the owner's reports: collecting
              what is owed is the till's daily work, not month-end reading. */}
          {canSettle && hasFeature('cari') && (
            <NavLink to={`/${tenant}/cari`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('cari')}>
              <WalletIcon />
              <span>Cari Hesap</span>
            </NavLink>
          )}
          {/* Paketinde yoksa kilitli (29 Eki 2026); yalnız patron görür, paketi o değiştirir. */}
          {isOwner && !hasFeature('cari') && !settingsLoading && (
            <LockedLink label="Cari Hesap" icon={<WalletIcon />} style={tone('cari')} onClick={() => askUpgrade('cari')} />
          )}
          {/* Kasiyerin yazıcı ayarları (kullanıcı, 20 Eki 2026). Patron aynı
              ekranı Ayarlar > Yazıcılar'da görüyor, ona ikinci bağlantı yok. */}
          {canSettle && !isOwner && !can('settings') && (
            <NavLink to={`/${tenant}/yazicilar`} className={({ isActive }) => (isActive ? 'on' : '')} style={tone('settings')}>
              <PrinterIcon />
              <span>Yazıcılar</span>
            </NavLink>
          )}
          {(canReports || canOpenSettings) && (
            <>
              {canReports && (
              <NavLink
                to={`/${tenant}/rapor`}
                style={tone('report')}
                className={({ isActive }) => (isActive && !search.get('bolum') ? 'on' : '')}
              >
                <TrendingUpIcon />
                <span>Rapor</span>
                <span className={`drawer__caret ${onReport ? 'is-open' : ''}`}>
                  <ChevronDownIcon />
                </span>
              </NavLink>
              )}
              {/* Rapor'un alt başlıkları (kullanıcı, 11 Eki 2026): ödeme,
                  ürünler ve siparişler kendi sayfasında. Rapor açıkken
                  listeleniyor, Ayarlar'daki gibi. */}
              {canReports && onReport && (
                <div className="drawer__sub" style={tone('report')}>
                  {REPORT_SECTIONS.map(([id, label]) => (
                    <NavLink
                      key={id}
                      to={`/${tenant}/rapor?bolum=${id}`}
                      className={() => (search.get('bolum') === id ? 'on' : '')}
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
              {/* Ayarlar carries six sections and every one of them was two
                  presses away: open the page, then find the section and expand
                  it. Listing them here makes each a single press, and makes
                  visible what the settings screen actually holds, a collapsed
                  page says nothing about what is inside it. */}
              {canOpenSettings && (
              <NavLink
                to={`/${tenant}/ayarlar`}
                style={tone('settings')}
                className={({ isActive }) =>
                  // Lit only while no section is chosen: once one is, the
                  // highlight belongs to it and two at once made neither
                  // read as the current place.
                  isActive && !search.get('bolum') ? 'on' : ''
                }
                onClick={() => {
                  // Already here: the press is about the list, so it folds.
                  // Coming from elsewhere: the navigation opens it by itself.
                  if (onSettings) setClosedOnSettings((v) => !v)
                }}
              >
                <SettingsIcon />
                <span>Ayarlar</span>
                {/* Wrapped because the icon components take no props, they
                    are bare svg, by design, so the rotation hangs on a span. */}
                <span className={`drawer__caret ${settingsOpen ? 'is-open' : ''}`}>
                  <ChevronDownIcon />
                </span>
              </NavLink>
              )}

              {canOpenSettings && settingsOpen && (
                <div className="drawer__sub" style={tone('settings')}>
                  {SETTINGS_SECTIONS.filter(([id]) => (id !== 'kiosk' || hasFeature('kiosk')) && (id !== 'sadakat' || hasFeature('damga')) && canSeeSection(id, isOwner, can)).map(([id, label]) => (
                    <NavLink
                      key={id}
                      to={`/${tenant}/ayarlar?bolum=${id}`}
                      className={() =>
                        // NavLink's own isActive ignores the query string, so
                        // every section would light up at once; the section in
                        // the address is what decides here.
                        search.get('bolum') === id && pathname.endsWith('/ayarlar') ? 'on' : ''
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          )}
          </>
          )}
        </nav>

        {/* Shown, not edited: two tills both left on K1 would issue duplicate
            receipt numbers, so the code stays visible everywhere, but it is
            changed in one place, Ayarlar > Sistem. A second editable copy kept
            its own useState and drifted out of step with the first. */}
        {/* Tarih bandı ve "Kasa K1" satırı kalktı (kullanıcı, 20 Eki 2026);
            kasa numarası Ayarlar > Yazıcılar'da. */}

        <div className="drawer__foot">
          <button type="button" onClick={toggle}>
            <SunMoonIcon />
            <span>{dark ? 'Açık mod' : 'Koyu mod'}</span>
          </button>
          <button type="button" onClick={onLogout}>
            <LogOutIcon />
            <span>Çıkış</span>
          </button>

          {/* Tucked away down here rather than pinned to the corner of every
              screen: it only matters when answering "is the deploy live yet?",
              and it was sitting on top of the till's own controls. */}
          <BuildStamp />
        </div>

        {/* Only where the panel is permanent. On a phone it slides away
            entirely, so there is nothing to narrow. */}
        <button
          type="button"
          className="drawer__rail-btn"
          onClick={toggleRail}
          title={rail ? 'Paneli genişlet' : 'Paneli daralt'}
          aria-label={rail ? 'Paneli genişlet' : 'Paneli daralt'}
        >
          {/* Narrowed, the divider sits right: the icon then reads as the
              panel that is there now, and pressing it widens back. */}
          {rail ? <PanelRightIcon /> : <PanelLeftIcon />}
        </button>
      </aside>
      </>
      )}

      {/* Yasal metin onayı: patron, sürüm onaylanmamışsa. */}
      <LegalGate />

      {/* 10 ve 20. dakika hatırlatması: hangi ekranda olursa olsun üstte. */}
      {trackAlert && (
        <NavLink to={`/${tenant}/takip`} className="track-alert" role="status">
          {trackAlert}
        </NavLink>
      )}

      <main className="pos__main">
        {/* Kurulum bitmemişse sayfanın üstünde, kendi satırında: sayfalar
            yüksekliklerini ana alandan alıyor ve çubuğu içlerine koymak
            her birinin kaydırmasını bozardı. */}
        <TrialBanner />
        <SetupProgress />
        <div className="pos__page">{children}</div>
      </main>

      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}
    </div>
  )
}

/** Ana ekran simgesi (icons.tsx'teki çizgi simgelerle aynı dil). */
function HomeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />
    </svg>
  )
}

/**
 * Her bölümün rengi (18 Eki 2026, kullanıcı: "hangi kategorideysem oranın
 * rengi olsun"). Ana ekrandaki kutularla aynı: Gel Al turuncu, Masalar mavi,
 * Mutfak yeşil. Simge hep bu renkte; seçiliyken zemin, çizgi ve yazı da.
 */
const TONES = {
  home: '#4f46e5',
  sale: '#f15a24',
  track: '#0891b2',
  menu: '#d97706',
  tables: '#2563eb',
  kitchen: '#16a34a',
  cari: '#0d9488',
  report: '#7c3aed',
  settings: '#64748b',
} as const

const tone = (k: keyof typeof TONES) => ({ '--tone': TONES[k] }) as CSSProperties

/**
 * Paketinde olmayan bölüm (29 Eki 2026, kullanıcı): menüde yerinde ama soluk
 * ve kilitli; basınca yükseltme penceresi açılır, sayfaya gitmez.
 */
function LockedLink({
  label,
  icon,
  style,
  onClick,
}: {
  label: string
  icon: ReactNode
  style?: CSSProperties
  onClick: () => void
}) {
  return (
    <a
      href="#"
      className="is-locked"
      style={style}
      aria-label={`${label} (paketinizde yok)`}
      onClick={(e) => {
        e.preventDefault()
        // Çekmeceyi kapatan üst onClick'e gitmesin: pencere çekmecenin üstünde açılıyor.
        e.stopPropagation()
        onClick()
      }}
    >
      {icon}
      <span>{label}</span>
      <i className="drawer__lock" aria-hidden="true">
        <LockIcon />
      </i>
    </a>
  )
}
